import {
  blueColors,
  grayColors,
  greenColors,
  redColors,
  yellowColors,
  borderRadius,
  transition,
  fontFamily
} from './primitive.tokens';

export const primaryPalette = {
  100: blueColors[100],
  200: blueColors[200],
  300: blueColors[300],
  400: blueColors[400],
  500: blueColors[500],
  600: blueColors[600],
  700: blueColors[700],
  800: blueColors[800],
  900: blueColors[900]
} as const;

const lightColorScheme = {
  primary: {
    color: blueColors[600],
    contrastColor: '#FFFFFF',
    hoverColor: blueColors[700],
    activeColor: blueColors[800]
  },

  surface: {
    0: '#FFFFFF',
    100: grayColors[100],
    200: grayColors[200],
    300: grayColors[300],
    400: grayColors[400],
    500: grayColors[500],
    600: grayColors[600],
    700: grayColors[700],
    800: grayColors[800],
    900: grayColors[900],
    1000: grayColors[1000],
    1100: grayColors[1100]
  },

  text: {
    color: grayColors[1000],
    hoverColor: grayColors[900],
    mutedColor: grayColors[600],
    hoverMutedColor: grayColors[700]
  },

  formField: {
    background: '#FFFFFF',
    disabledBackground: grayColors[100],
    filledBackground: grayColors[100],
    filledHoverBackground: grayColors[300],
    filledFocusBackground: grayColors[300],
    borderColor: grayColors[500],
    hoverBorderColor: grayColors[500],
    focusBorderColor: blueColors[600],
    invalidBorderColor: redColors[600],
    color: grayColors[1000],
    disabledColor: grayColors[500],
    placeholderColor: grayColors[1000],
    invalidPlaceholderColor: redColors[600],
    floatLabelColor: grayColors[600],
    floatLabelFocusColor: blueColors[600],
    floatLabelActiveColor: grayColors[600],
    floatLabelInvalidColor: redColors[600],
    iconColor: '{primary.color}',
    shadow: '0 0 0 0.2rem ' + blueColors[100]
  },

  focusRing: {
    width: '2px',
    style: 'solid',
    color: '{primary.color}',
    offset: '-2px',
    shadow: 'none'
  },

  highlight: {
    background: blueColors[100],
    focusBackground: blueColors[200],
    color: blueColors[800],
    focusColor: blueColors[900]
  },

  success: {
    color: greenColors[700],
    contrastColor: '#FFFFFF',
    hoverColor: greenColors[700],
    activeColor: greenColors[700],
    background: greenColors[100]
  },

  danger: {
    color: redColors[600],
    contrastColor: '#FFFFFF',
    hoverColor: redColors[700],
    activeColor: redColors[700],
    background: redColors[100]
  },

  warn: {
    color: yellowColors[600],
    contrastColor: grayColors[1100],
    hoverColor: yellowColors[700],
    activeColor: yellowColors[700],
    background: yellowColors[100]
  },

  info: {
    color: blueColors[600],
    contrastColor: '#FFFFFF',
    hoverColor: blueColors[700],
    activeColor: blueColors[800],
    background: blueColors[100]
  },

  help: {
    color: blueColors[500],
    contrastColor: '#FFFFFF',
    hoverColor: blueColors[600],
    activeColor: blueColors[700],
    background: blueColors[100]
  },

  mask: {
    background: 'rgba(0, 0, 0, 0.4)',
    color: grayColors[200]
  },

  overlay: {
    select: {
      background: '#FFFFFF',
      borderColor: grayColors[500],
      color: grayColors[1100]
    },
    popover: {
      background: '#FFFFFF',
      borderColor: grayColors[300],
      color: grayColors[1100]
    },
    modal: {
      background: '#FFFFFF',
      borderColor: grayColors[300],
      color: grayColors[1100]
    }
  },

  navigation: {
    item: {
      focusBackground: grayColors[100],
      activeBackground: blueColors[100],
      color: grayColors[800],
      focusColor: grayColors[900],
      activeColor: blueColors[700],
      icon: {
        color: grayColors[600],
        focusColor: grayColors[700],
        activeColor: blueColors[600]
      }
    },
    submenuLabel: {
      background: 'transparent',
      color: grayColors[600]
    },
    submenuIcon: {
      color: grayColors[500],
      focusColor: grayColors[600],
      activeColor: blueColors[500]
    }
  }
} as const;

const darkColorScheme = {
  primary: {
    color: blueColors[400],
    contrastColor: grayColors[1100],
    hoverColor: blueColors[300],
    activeColor: blueColors[200]
  },

  surface: {
    0: grayColors[1000],
    100: grayColors[900],
    200: grayColors[800],
    300: grayColors[700],
    400: grayColors[600],
    500: grayColors[500],
    600: grayColors[400],
    700: grayColors[300],
    800: grayColors[200],
    900: grayColors[100],
    1000: grayColors[0],
    1100: grayColors[1100]
  },

  text: {
    color: grayColors[0],
    hoverColor: grayColors[100],
    mutedColor: grayColors[400],
    hoverMutedColor: grayColors[300]
  },

  formField: {
    background: grayColors[900],
    disabledBackground: grayColors[800],
    filledBackground: grayColors[800],
    filledHoverBackground: grayColors[800],
    filledFocusBackground: grayColors[800],
    borderColor: grayColors[700],
    hoverBorderColor: grayColors[600],
    focusBorderColor: blueColors[400],
    invalidBorderColor: redColors[400],
    color: grayColors[100],
    disabledColor: grayColors[500],
    placeholderColor: grayColors[500],
    invalidPlaceholderColor: redColors[400],
    floatLabelColor: grayColors[400],
    floatLabelFocusColor: blueColors[400],
    floatLabelActiveColor: grayColors[400],
    floatLabelInvalidColor: redColors[400],
    iconColor: grayColors[400],
    shadow: '0 0 0 0.2rem ' + blueColors[800]
  },

  highlight: {
    background: blueColors[800],
    focusBackground: blueColors[700],
    color: blueColors[100],
    focusColor: blueColors[100]
  },

  success: {
    color: greenColors[400],
    contrastColor: grayColors[1100],
    hoverColor: greenColors[300],
    activeColor: greenColors[200],
    background: greenColors[700]
  },

  danger: {
    color: redColors[400],
    contrastColor: grayColors[1100],
    hoverColor: redColors[300],
    activeColor: redColors[200],
    background: redColors[700]
  },

  warn: {
    color: yellowColors[400],
    contrastColor: grayColors[1100],
    hoverColor: yellowColors[300],
    activeColor: yellowColors[200],
    background: yellowColors[700]
  },

  info: {
    color: blueColors[400],
    contrastColor: grayColors[1100],
    hoverColor: blueColors[300],
    activeColor: blueColors[200],
    background: blueColors[700]
  },

  help: {
    color: blueColors[400],
    contrastColor: grayColors[1100],
    hoverColor: blueColors[300],
    activeColor: blueColors[200],
    background: blueColors[900]
  },

  mask: {
    background: 'rgba(0, 0, 0, 0.6)',
    color: grayColors[200]
  },

  overlay: {
    select: {
      background: grayColors[900],
      borderColor: grayColors[700],
      color: grayColors[100]
    },
    popover: {
      background: grayColors[900],
      borderColor: grayColors[700],
      color: grayColors[100]
    },
    modal: {
      background: grayColors[900],
      borderColor: grayColors[700],
      color: grayColors[100]
    }
  },

  navigation: {
    item: {
      focusBackground: grayColors[800],
      activeBackground: blueColors[900],
      color: grayColors[200],
      focusColor: grayColors[100],
      activeColor: blueColors[300],
      icon: {
        color: grayColors[400],
        focusColor: grayColors[300],
        activeColor: blueColors[400]
      }
    },
    submenuLabel: {
      background: 'transparent',
      color: grayColors[400]
    },
    submenuIcon: {
      color: grayColors[500],
      focusColor: grayColors[400],
      activeColor: blueColors[400]
    }
  }
} as const;

export const sharedSemanticTokens = {
  fontFamily: fontFamily.sans,

  borderRadius: {
    none: borderRadius.none,
    xs: borderRadius.sm,
    sm: borderRadius.sm,
    md: borderRadius.md,
    lg: borderRadius.lg,
    xl: borderRadius.xl,
    xxl: borderRadius.xxl,
    xxxl: borderRadius.xxxl,
    full: borderRadius.full
  },

  iconSize: '1rem',
  anchorGutter: '0.5rem',

  content: {
    padding: '1rem',
    gap: '0.5rem'
  },

  disabledOpacity: '0.6',
  transitionDuration: transition.duration.normal
} as const;

export const semanticTokens = {
  primary: primaryPalette,
  colorScheme: {
    light: lightColorScheme,
    dark: darkColorScheme
  },
  ...sharedSemanticTokens
};
